import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import * as PropTypes from 'prop-types';
import { Text, mergeStyleSets, Stack, Link } from '@fluentui/react';
import { useSelector } from 'react-redux';
import { ShowingResultsFor } from '@appsource/components/ShowingResultsFor';
import { SortingDropdown } from '@shared/components/sortingDropdown';
import { Constants } from '@shared/utils/constants';
import { routes } from '@shared/routerHistory';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThumbsUpDown } from '@appsource/components/thumbsUpDown';
export const TitleComponent = ({ localizedTitle, previousSearchText = '', showingResultsForText = '', titleAsH1 = true })=>{
    return showingResultsForText.length > 0 ? /*#__PURE__*/ _jsx(ShowingResultsFor, {
        showingResultsForText: showingResultsForText,
        previousSearchText: previousSearchText
    }) : /*#__PURE__*/ _jsx(Text, {
        as: titleAsH1 ? 'h1' : 'h2',
        variant: "xLarge",
        className: "galleryTitle",
        children: localizedTitle
    });
};
const contentStyles = mergeStyleSets({
    subTitle: {
        marginTop: 8
    },
    container: {
        marginBottom: 20
    },
    leftSpace: {
        paddingLeft: 6
    }
});
const { Popularity, RecentlyUpdated, BestMatch, RatingAverage } = Constants.SearchSortingOption;
const nonDefaultSortingOptions = [
    Popularity,
    RecentlyUpdated,
    RatingAverage
];
const { marketplaceServices, marketplace, marketplaceCloudsIndustry } = routes;
const viewToRouteMap = {
    [Constants.Views.appGallery]: marketplace,
    [Constants.Views.serviceGallery]: marketplaceServices,
    [Constants.Views.cloudsIndustryGallery]: marketplaceCloudsIndustry
};
export const FilteredGalleryHeader = ({ otherGalleriesLinks, subTitle, numResults, localizedTitle, hideSortingDropdown, titleAsH1 }, context)=>{
    var _searchParams_get;
    const { showingResultsFor: showingResultsForText = '', previousSearchText = '', currentView } = useSelector(({ search: { showingResultsFor, previousSearchText }, config: { currentView, featureFlags: { sortByRatingEnabled, allNewSortsEnabled, sortByRecentlyUpdatedEnabled } } })=>({
            showingResultsFor,
            previousSearchText,
            currentView,
            sortByRatingEnabled,
            allNewSortsEnabled,
            sortByRecentlyUpdatedEnabled
        }));
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sortByQueryParam = searchParams.get(Constants.FilterQuery.sortBy);
    let defaultSortValue = BestMatch;
    if (sortByQueryParam && nonDefaultSortingOptions.includes(sortByQueryParam)) {
        defaultSortValue = sortByQueryParam;
    }
    const baseRoute = viewToRouteMap[`${currentView}`] || marketplace;
    const { container, leftSpace, subTitle: subTitleClass } = contentStyles;
    const handleClearSearch = ()=>{
        const url = context.buildHref(baseRoute, {}, {
            [Constants.FilterQuery.search]: ''
        }, false);
        navigate(url);
    };
    const shouldDisplayClearSearch = previousSearchText || ((_searchParams_get = searchParams.get('search')) === null || _searchParams_get === void 0 ? void 0 : _searchParams_get.length) > 0;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                role: "status",
                "aria-label": `${numResults} items found`
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                horizontal: true,
                horizontalAlign: "space-between",
                className: container,
                wrap: true,
                tokens: {
                    childrenGap: 6
                },
                children: [
                    /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(TitleComponent, {
                                localizedTitle: localizedTitle,
                                previousSearchText: previousSearchText,
                                showingResultsForText: showingResultsForText,
                                titleAsH1: titleAsH1
                            }),
                            /*#__PURE__*/ _jsxs(Stack, {
                                tokens: {
                                    childrenGap: 4
                                },
                                horizontal: true,
                                className: subTitleClass,
                                wrap: true,
                                children: [
                                    /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            block: true,
                                            children: subTitle
                                        })
                                    }),
                                    !shouldDisplayClearSearch && otherGalleriesLinks && /*#__PURE__*/ _jsx(Stack.Item, {
                                        className: leftSpace,
                                        children: otherGalleriesLinks
                                    }),
                                    /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: shouldDisplayClearSearch && /*#__PURE__*/ _jsx(Link, {
                                            onClick: ()=>handleClearSearch(),
                                            underline: true,
                                            as: "button",
                                            children: context.loc('ClearSearch', 'Clear search')
                                        })
                                    })
                                ]
                            }),
                            shouldDisplayClearSearch && otherGalleriesLinks && /*#__PURE__*/ _jsx(Stack.Item, {
                                tokens: {
                                    margin: '8px 0'
                                },
                                children: otherGalleriesLinks
                            }),
                            !!showingResultsForText.length && /*#__PURE__*/ _jsx(Stack.Item, {
                                children: /*#__PURE__*/ _jsx(ThumbsUpDown, {
                                    localizedQuestion: context.loc('Feedback_ShowingResultsFor', "Are these results what you're looking for?")
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Stack, {
                            verticalAlign: "start",
                            horizontalAlign: "end",
                            children: numResults > 0 && /*#__PURE__*/ _jsx(Stack.Item, {
                                children: /*#__PURE__*/ _jsx(SortingDropdown, {
                                    defaultKey: defaultSortValue,
                                    route: baseRoute,
                                    hide: hideSortingDropdown
                                })
                            })
                        })
                    })
                ]
            })
        ]
    });
};
FilteredGalleryHeader.contextTypes = {
    loc: PropTypes.func,
    buildHref: PropTypes.func
};
